// src/features/links/LinkDetailPanel.tsx
// IR-1047 (Rotem 2026-06-22) — right-side detail panel for a Join Links row.
//
// Rotem's ask: the cramped inline 28px thumbnail isn't a usable way to show the
// target group's picture — open a card on the right with the group's details
// (picture large + name/bio/members) when a row is clicked, close it with an X.
//
// The panel renders the TARGET group the link points at: large picture, Name and
// Bio (each with the English translation shown beneath when present), member
// count, platform, the invite link (with copy), plus the operational/approval
// state and source attribution. The per-row "Fetch preview" button is available
// here too, so an analyst can resolve/refresh a preview from the panel.
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { CopyButton } from "./CopyButton";
import { PlatformIcon } from "./PlatformIcon";
import { OP_STATUS_LABELS, PLATFORM_LABELS } from "./labels";
import { linkUrl } from "./linkUrl";
import { targetPreviewPhotoUrl } from "./photoUrl";
import { FetchPreviewButton } from "./previewCells";
import type { LinkOnboardingRow } from "./types";

interface Props {
  /** The row to show, or null when the panel is closed. */
  row: LinkOnboardingRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // IR-1047 — resolve/refresh the target-group preview from inside the panel.
  onResolvePreview: (id: number) => void;
  resolvingPreviewId?: number | null;
}

/** A labelled field block. Renders nothing when there's no value to show. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] uppercase tracking-wide text-[var(--iris-fg-4)]">{label}</div>
      <div className="text-sm text-[var(--iris-fg-1)]">{children}</div>
    </div>
  );
}

function emDash() {
  return <span className="text-[var(--iris-fg-4)]">—</span>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

export function LinkDetailPanel({
  row,
  open,
  onOpenChange,
  onResolvePreview,
  resolvingPreviewId,
}: Props) {
  const preview = row?.target_preview ?? null;
  const photo = preview ? targetPreviewPhotoUrl(preview.photo_url) : null;
  const title = preview?.name?.trim() || "Group details";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="w-[420px] max-w-[92vw] overflow-y-auto p-5"
      >
        {row && (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <SheetTitle className="truncate">{title}</SheetTitle>
                <SheetDescription className="mt-0.5 flex items-center gap-1.5">
                  <PlatformIcon platform={row.platform} />
                  <span>{PLATFORM_LABELS[row.platform]}</span>
                </SheetDescription>
              </div>
              <SheetClose aria-label="Close" className="iris-btn shrink-0 px-2 text-sm">
                ✕
              </SheetClose>
            </div>

            {/* Large picture — the whole point of the panel (Rotem 2026-06-22). */}
            <div className="flex justify-center">
              {photo ? (
                <img
                  src={photo}
                  alt={preview?.name ?? "Group picture"}
                  className="h-32 w-32 rounded-full border border-[var(--iris-line-2)] object-cover"
                />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-full border border-dashed border-[var(--iris-line-2)] text-xs text-[var(--iris-fg-4)]">
                  No picture
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              <Field label="Name">
                {preview?.name ? (
                  <div>
                    <span>{preview.name}</span>
                    {preview.name_en &&
                      preview.name_en.trim() !== "" &&
                      preview.name_en.trim() !== preview.name.trim() && (
                        <div className="text-xs text-[var(--iris-fg-3)]">{preview.name_en}</div>
                      )}
                  </div>
                ) : (
                  emDash()
                )}
              </Field>

              <Field label="Bio">
                {preview?.bio ? (
                  <div>
                    <p className="whitespace-pre-wrap text-sm text-[var(--iris-fg-2)]">
                      {preview.bio}
                    </p>
                    {preview.bio_en &&
                      preview.bio_en.trim() !== "" &&
                      preview.bio_en.trim() !== preview.bio.trim() && (
                        <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--iris-fg-3)]">
                          {preview.bio_en}
                        </p>
                      )}
                  </div>
                ) : (
                  emDash()
                )}
              </Field>

              <Field label="Members">
                {preview?.member_count != null ? (
                  <span className="tabular-nums">
                    {preview.member_count.toLocaleString("en-US")}
                  </span>
                ) : (
                  emDash()
                )}
              </Field>

              <Field label="Link">
                <div className="flex items-center gap-1">
                  <a
                    href={linkUrl(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block min-w-0 truncate font-mono text-xs underline decoration-dotted underline-offset-2 hover:text-[var(--iris-fg-1)]"
                    title={row.link}
                  >
                    {linkUrl(row)}
                  </a>
                  <CopyButton text={linkUrl(row)} />
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Operational Status">{OP_STATUS_LABELS[row.operational_status]}</Field>
                <Field label="Approval Required">{row.approval_required ? "Yes" : "No"}</Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Risk Score">
                  {row.risk_score != null ? row.risk_score.toFixed(2) : emDash()}
                </Field>
                <Field label="Classification">
                  {row.classification && row.classification.length > 0
                    ? row.classification.join(", ")
                    : emDash()}
                </Field>
              </div>

              <Field label="Avatar (finder)">
                {row.avatar_finder ? (
                  <span>
                    {row.avatar_finder.name}{" "}
                    <span className="text-[var(--iris-fg-4)]">({row.avatar_finder.platform})</span>
                  </span>
                ) : (
                  emDash()
                )}
              </Field>

              <Field label={`Source Groups (${row.source_group_count})`}>
                {row.source_groups.length > 0 ? (
                  <ul className="space-y-0.5 text-xs text-[var(--iris-fg-2)]">
                    {row.source_groups.map((g) => (
                      <li key={g.entity_id} className="truncate">
                        {g.display_name ?? `#${g.entity_id}`}
                      </li>
                    ))}
                  </ul>
                ) : (
                  emDash()
                )}
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="First Seen">{formatDate(row.first_seen_at)}</Field>
                <Field label="Last Seen">{formatDate(row.last_seen_at)}</Field>
              </div>

              <Field label="Appearances">
                <span className="tabular-nums">{row.appearances.toLocaleString("en-US")}</span>
              </Field>
            </div>

            <div className="border-t border-[var(--iris-line-2)] pt-4">
              <FetchPreviewButton
                preview={preview}
                pending={resolvingPreviewId === row.id}
                onFetch={() => onResolvePreview(row.id)}
              />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
